<template>
  <ModalDialog
    ref="dialog"
    title="Batch Create Inventory Bins"
    :submitting="submitting"
    submitTitle="Create"
    :submitDisabled="submitDisabled"
    :showSubmit="!hasCreatedList"
    icon="fa-inventory"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3" v-if="!hasCreatedList">
      <FormRow label="Account Location">
        <AccountLocationSelect v-model="binConfig.accountLocationId" field="accountLocationId" />
      </FormRow>
      <FormRow label="Description">
        <FormInputText v-model="binConfig.description" field="description" placeholder="Bin Description" />
      </FormRow>
      <FormRow label="Labels">
        <FormLabelTag popupName="Bin Labels" v-model="binConfig.labelList" labelType="inventoryBin" field="labelList" />
      </FormRow>        
      <FormHorizontalRule />
      <ContentTitle>Batch Details</ContentTitle>
      <FormRow label="Code Type">
        <FormOption label="Simple" name="codeStyle" :setting="InventoryBinCodeTypes.simple" v-model="binConfig.codeType" inline="true" />
        <FormOption label="Detailed" name="codeStyle" :setting="InventoryBinCodeTypes.detailed" v-model="binConfig.codeType" inline="true" />
      </FormRow>
      <div v-show="isCodeTypeSimple">
        <FormRow label="Bin List">
          <div class="row mb-1" v-for="(simpleBin, index) of simpleBinList" :key="`${index}`">
            <div class="col-10">
              <FormInputText :value="simpleBin" @input="updateSimpleBinValue(index, $event)" placeholder="Bin Code" />
            </div>
            <div class="col-2">
              <FormButton class="btn-default btn-sm" @click="removeSimpleBin(index)"><i class="far fa-trash-alt" /></FormButton>
            </div>
          </div>
          <FormButton class="btn-white btn-xs mt-1" @click="newSimpleBin"><i class="far fa-plus" /> Add Bin</FormButton>
        </FormRow>
      </div>
      <div v-show="isCodeTypeDetailed">
        <FormRow label="Zone" :fieldCount="2">
          <template v-slot:field1>
            <FormInputText v-model="binConfig.zone" field="zone" placeholder="Bin Zone" prepend="Name" note="If a number, will increment for each count required" />
          </template>
          <template v-slot:field2>
            <FormInputText type="number" v-model="binConfig.zoneCount" field="zoneCount" placeholder="Zone Count" prepend="Count" />
          </template>
        </FormRow>
        <FormRow label="Aisle" :fieldCount="2">
          <template v-slot:field1>
            <FormInputText v-model="binConfig.aisle" field="aisle" placeholder="Bin Aisle" prepend="Name" note="If a number, will increment for each count required" />
          </template>
          <template v-slot:field2>
            <FormInputText type="number" v-model="binConfig.aisleCount" field="aisleCount" placeholder="Aisle Count" prepend="Count" />
          </template>
        </FormRow>
        <FormRow label="Bay" :fieldCount="2">
          <template v-slot:field1>
            <FormInputText v-model="binConfig.bay" field="bay" placeholder="Bin Bay" prepend="Name" note="If a number, will increment for each count required" />
          </template>
          <template v-slot:field2>
            <FormInputText type="number" v-model="binConfig.bayCount" field="bayCount" placeholder="Bay Count" prepend="Count" />
          </template>
        </FormRow>
        <FormRow label="Level" :fieldCount="2">
          <template v-slot:field1>
            <FormInputText v-model="binConfig.level" field="level" placeholder="Bin Level" prepend="Name" note="If a number, will increment for each count required" />
          </template>
          <template v-slot:field2>
            <FormInputText type="number" v-model="binConfig.levelCount" field="levelCount" placeholder="Level Count" prepend="Count" />
          </template>
        </FormRow>
        <FormRow label="Sample Code">
          <FormStaticLabel>{{sampleDetailedCode}}</FormStaticLabel>
        </FormRow>
        <FormRow label="Bin List">
          <FormStaticLabel>{{detailedBinList.length}} Bins will be generated<br />
          <small>{{detailedBinList.slice(0, 6).map(bin => bin.code).join(', ')}}<span v-if="detailedBinList.length > 6">...</span></small>
          </FormStaticLabel>
        </FormRow>
      </div>
    </FormGroup>

    <div v-if="hasCreatedList">
      <ContentTitle>{{createdBinIdList.length}} Inventory Bins Created</ContentTitle>
      <FormButton class="btn-success" @click="printCreatedBinList"><i class="far fa-print" /> Print Labels</FormButton>
    </div>

  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'

import AccountLocationSelect from '@/modules/admin/components/accountLocationSelect'

import { InventoryBinCodeTypes } from '@/store/types'

export default {
  name: 'InventoryBinBatchDialog',
  mixins: [
    ModalDialogMixin,
    form
  ],
  components: {
    ModalDialog,
    AccountLocationSelect
  },
  props: {
  },
  data() {
    return {
      loading: false,
      loadingError: '',

      binConfig: {
        codeType: InventoryBinCodeTypes.simple,
        zone: '',
        zoneCount: 1,
        aisle: '',
        aisleCount: 1,
        bay: '',
        bayCount: 1,
        level: '',
        levelCount: 1,
        description: '',
        accountLocationId: null,
        labelList: []
      },

      simpleBinList: [''],

      hasCreatedList: false,
      createdBinIdList: [],

      dialogForm: {},
      submitting: false
    }
  },
  created() {
    this.InventoryBinCodeTypes = InventoryBinCodeTypes
  },
  mounted() {
  },
  computed: {
    isCodeTypeSimple() {
      return this.binConfig.codeType == InventoryBinCodeTypes.simple
    },
    isCodeTypeDetailed() {
      return this.binConfig.codeType == InventoryBinCodeTypes.detailed
    },
    submitDisabled() {
      return (this.finalBinList.length == 0)
    },
    finalBinList() {
      if(this.isCodeTypeSimple) {
        return _.uniq(_.compact(this.simpleBinList)).map(code => {
          return {
            code: code
          }
        })
      }
      
      return this.detailedBinList
    },
    sampleDetailedCode() {
      return _.compact([
        this.sectionCodeGen(this.binConfig.zone, 'Z'),
        this.sectionCodeGen(this.binConfig.aisle, 'A'),
        this.sectionCodeGen(this.binConfig.bay, 'B'),
        this.sectionCodeGen(this.binConfig.level, 'L')
      ]).join('-')
    },
    detailedBinList() {
      var zoneCount = this.binConfig.zoneCount || 1
      var aisleCount = this.binConfig.aisleCount || 1
      var bayCount = this.binConfig.bayCount || 1
      var levelCount = this.binConfig.levelCount || 1

      var outputList = []

      var getSectionPrefix = (name, index) => {
        if(!name)
          return ''
        if(isNaN(parseInt(name)))
          return name
        return parseInt(name) + index
      }

      for(let z=0; z<zoneCount; z++) {
        for(let a=0; a<aisleCount; a++) {
          for(let b=0; b<bayCount; b++) {
            for(let l=0; l<levelCount; l++) {
              let zoneName = getSectionPrefix(this.binConfig.zone, z)
              let aisleName = getSectionPrefix(this.binConfig.aisle, a)
              let bayName = getSectionPrefix(this.binConfig.bay, b)
              let levelName = getSectionPrefix(this.binConfig.level, l)

              let code = _.compact([
                this.sectionCodeGen(zoneName, 'Z'),
                this.sectionCodeGen(aisleName, 'A'),
                this.sectionCodeGen(bayName, 'B'),
                this.sectionCodeGen(levelName, 'L')
              ]).join('-')

              if(!code)
                continue

              outputList.push({
                code: code,
                zone: zoneName,
                aisle: aisleName,
                bay: bayName,
                level: levelName,
              })
            }
          }
        }
      }

      outputList = _.uniqBy(_.compact(outputList), 'code')

      return outputList
    }
  },
  methods: {
    show() {
      // Reset form
      this.dialogForm = {}
      this.submitting = false

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.$data.dialogForm = this.clearFormResponse()

      // Not going to work. Need info for detailed version
      var createBinList = this.finalBinList.map(binInfo => {
        return {
          codeType: this.binConfig.codeType,
          description: this.binConfig.description,
          code: binInfo.code,
          zone: binInfo.zone || '',
          aisle: binInfo.aisle || '',
          bay: binInfo.bay || '',
          level: binInfo.level || '',
          highlightColour: '#808080',
          accountLocationId: this.binConfig.accountLocationId,
          labelList: this.binConfig.labelList.map(label => label.id)
        }
      })

      if(createBinList.length == 0) {
        this.$data.dialogForm.errorMessage = 'No bins to be created'
        return
      }

      this.submitting = true
      this.$apiService.inventoryService.createInventoryBinBatch({ binList: createBinList })
      .then((response) => {
        this.createdBinIdList = response.data
        this.hasCreatedList = true

        //this.submitting = false
        //this.modalClosed(true)
        //this.hide()

        // TODO: Display option to print the labels
      })
      .catch(err => {
        this.submitting = false
        this.$data.dialogForm = this.processFormResponse(err)
      })
    },
    sectionCodeGen(value, prefix) {
      value = ('' + value).trim()
      if(!value)
        return ''

      // If a complete number, use prefix
      if(!isNaN(parseInt(value)))
        return `${prefix}${value}`

      return value
    },
    newSimpleBin() {
      this.simpleBinList.push('')
    },
    updateSimpleBinValue(index, value) {
      this.$set(this.simpleBinList, index, value)
    },
    removeSimpleBin(index) {
      this.simpleBinList.splice(index, 1)
    },
    printCreatedBinList() {
      this.$apiService.inventoryService.generatePrintInventoryBinLabelListURL(this.createdBinIdList, { download: true, label: true, inventorybinlabel: true })
      .then(response => {
        window.location.href = response.data.url
      })
      .catch(err => {
        console.error(err)
        this.displayNetworkError(err, 'Print Error')
      })
    }
  }
}
</script>